import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Snackbar,

} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../Storage/Redux/store";
import { useGetNextQuizQuery } from "../../Apis/quizApi";
import {
  useGetLeaderboardQuery,
  useStartQuizMutation,useGetUserProgressReportQuery
} from "../../Apis/progressApi";
import { userModel } from "../../Interfaces";
import MainQuiz from "./MainQuiz";
import { Swiper, SwiperSlide } from 'swiper/react';
import {  Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Share } from "@mui/icons-material";

export default function QuizHome() {
  const userData: userModel = useSelector(
    (state: RootState) => state.userAuthStore
  );

  const {
    data: nextQuizData,
    isLoading,
    isError,
  } = useGetNextQuizQuery(userData?.nameid, { skip: !userData });

  const [startQuizMutation] = useStartQuizMutation();
  const [quizStarted, setQuizStarted] = useState(false);
  const [progressId, setProgressId] = useState<number | null>(null);
  const [startingQuiz, setStartingQuiz] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  
  

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info" as "success" | "error" | "info" | "warning",
  });

  const { data: leaderboardData, refetch } = useGetLeaderboardQuery();
  const { data: userProgressData, isLoading: progressLoading } = useGetUserProgressReportQuery(undefined, {
    skip: !nextQuizData?.message, 
  });
  const swiperRef = useRef<SwiperType |null>(null);

  // ✅ Always call hooks at top
  useEffect(() => {
    refetch();
  }, [refetch]);



  const getRankBadge = (rank: number): string => {
    if (rank === 1) return "🥇";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    return `#${rank}`;
  };

  const getScoreColor = (score: number) => {
    if (score >= 40) return "success.main";
    if (score >= 30) return "warning.main";
    return "error.main";
  };
  
    const handleShare = async (progressId:number) => {
    if (!progressId) return;

    const shareLink = `${window.location.origin}/Register?ref=${progressId}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Try this quiz!",
          text: "Give it a try and see how you do!",
          url: shareLink,
        });

        setSnackbar({open:true,message:"Share your link! Earn 20 bonus points when someone registers using it.",severity:'info'});
      } catch {
        try {
          await navigator.clipboard.writeText(shareLink);
          setSnackbar({
            open: true,
            message: "Share dialog closed. Link copied to clipboard!",
            severity: "warning",
          });

        } catch {
          setSnackbar({
            open: true,
            message: "Copy this link manually: " + shareLink,
            severity: "warning",
          });
        }
      }
    } else {
      try {
          await navigator.clipboard.writeText(shareLink);
          setSnackbar({
            open: true,
            message: "Share dialog closed. Link copied to clipboard!",
            severity: "warning",
          });

        } catch {
          setSnackbar({
            open: true,
            message: "Copy this link manually: " + shareLink,
            severity: "warning",
          });
        }
    }
  };


  const handleStartQuiz = async () => {
    if (!nextQuizData) return;

    setStartingQuiz(true);
    try {
      const progress = await startQuizMutation(nextQuizData.id).unwrap();
      setProgressId(progress.id);
      setQuizStarted(true);
    } catch (error: any) {
      console.error("Failed to start quiz:", error);
      let msg = "Failed to start quiz. Please try again.";
      if (error?.data?.message) msg = error.data.message;
      else if (error?.status === 401) msg = "Please log in to start the quiz.";
      else if (error?.status === 404) msg = "Quiz not found. Please try again.";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setStartingQuiz(false);
    }
  };

  // 🌀 Conditional rendering
  if (isLoading) {
    return (
      <Container
        sx={{
          my: 5,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container sx={{ my: 5 }}>
        <Alert severity="error" sx={{ textAlign: "center" }}>
          Failed to load quiz details.
        </Alert>
      </Container>
    );
  }

  if (!nextQuizData) {
    return (
      <Container sx={{ my: 5 }}>
        <Alert severity="info" sx={{ textAlign: "center" }}>
          No quiz available at this time.
        </Alert>
      </Container>
    );
  }

  if (nextQuizData?.message) {
    const topTen = leaderboardData?.topTen || [];
    const myProgress = userProgressData || [];
    const totalScore = myProgress.reduce((sum: number, item: any) => sum + item.score, 0);
    const isCurrentUser = (name: string): boolean => name === userData.email;

    return (
      <Container sx={{ my: 5 }}>
        <Card elevation={3} sx={{ textAlign: "center", mb: 3 }}>
          <CardContent>
            <Typography variant="h4" sx={{ color: "#512384", fontWeight: 600 }}>
              Congratulations!
            </Typography>
            <Typography variant="h6" color="success.main" sx={{ my: 2 }}>
              You have completed all available quizzes.
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Check back later for new challenges!
            </Typography>
          </CardContent>
          <CardContent
            sx={{
              display: "flex",
              justifyContent: "space-around",
              bgcolor: "#fafafa",
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ textTransform: "uppercase" }}
              >
                Your Total Score
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontWeight: 800, color: "#512384" }}
              >
                {totalScore}
              </Typography>
            </Box>
            <Box sx={{ borderLeft: "1px solid #ddd" }} /> {/* Divider */}
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ textTransform: "uppercase" }}
              >
                Quizzes Completed
              </Typography>
              <Typography
                variant="h3"
                sx={{ fontWeight: 800, color: "#eb3c96" }}
              >
                {myProgress.length}
              </Typography>
            </Box>
          </CardContent>
        </Card>
        <Card elevation={3} sx={{ my: 5 }}>
          <CardHeader
            title={
              <Typography variant="h6" sx={{ color: "white", fontWeight: 600 }}>
                User Progress
              </Typography>
            }
            sx={{ background: "#512384" }}
          />
          <Box
            sx={{ p: 2, bgcolor: "#f0f7ff", borderBottom: "1px solid #e0e0e0" }}
          >
            <Typography
              variant="body2"
              sx={{ color: "#512384", fontWeight: 500 }}
            >
              🌟 <strong>Boost your score!</strong> Haven't earned a bonus yet?
              Share the link using the button below. You'll get{" "}
              <strong>+20 points for EACH quiz</strong> for the first friend who
              joins using your link!
            </Typography>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                  <TableCell>
                    <strong>Quiz Name</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Score</strong>
                  </TableCell>
                  <TableCell align="center">
                    <strong>Bonus Awarded</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {progressLoading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <CircularProgress size={24} sx={{ my: 2 }} />
                    </TableCell>
                  </TableRow>
                ) : myProgress.length > 0 ? (
                  myProgress.map((item: any) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="center">
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            color: getScoreColor(item.score),
                          }}
                        >
                          {item.score}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {item.bonusAwarded ? (
                          <Chip
                            label="+20"
                            color="success"
                            size="small"
                            variant="filled"
                          />
                        ) : (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Share />}
                            onClick={() => handleShare(item.id)}
                            sx={{
                              background:
                                "linear-gradient(45deg, #512384 30%, #7b1fa2 90%)",
                              color: "white",
                              borderRadius: "20px",
                              textTransform: "none",
                              px: 2,
                              boxShadow: "0 3px 5px 2px rgba(81, 35, 132, .3)",
                              "&:hover": {
                                background:
                                  "linear-gradient(45deg, #3c1a63 30%, #512384 90%)",
                              },
                            }}
                          >
                           Share 
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No progress data found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Card elevation={3}>
          <CardHeader
            title={
              <Typography variant="h6" sx={{ color: "white", fontWeight: 600 }}>
                🏆 Top 10 Leaderboard
              </Typography>
            }
            sx={{ background: "#512384" }}
          />

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rank#</TableCell>
                  <TableCell>Full Name</TableCell>
                  <TableCell>Total Scores</TableCell>
                  <TableCell>Quiz Taken</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topTen.map((participant: any) => {
                  const isUser = isCurrentUser(participant.username);
                  return (
                    <TableRow
                      key={participant.userId}
                      sx={{
                        backgroundColor: isUser
                          ? "rgba(81, 35, 132, 0.05)"
                          : "inherit",
                      }}
                    >
                      <TableCell>
                        <strong>{getRankBadge(participant.rank)}</strong>
                      </TableCell>
                      <TableCell>
                        <Typography
                          fontWeight={600}
                          color={isUser ? "primary.main" : "text.primary"}
                        >
                          {participant.fullName}
                          {isUser && " 👤"}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{ color: getScoreColor(participant.totalScore) }}
                      >
                        <strong>{participant.totalScore}</strong>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={participant.quizzesTaken}
                          color="info"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>
    );
  }

  if (quizStarted && progressId) {
    return (
      <MainQuiz
        quiz={nextQuizData}
        progressId={progressId}
        username={userData?.fullName || userData?.email || "User"}
        onClose={() => setQuizStarted(false)}
      />
    );
  }

  const Quiz = nextQuizData;
  const maxSteps = Quiz.quizImages.length;

  return (
    <Container sx={{ my:2}}>
      <Card elevation={3}>
        <CardHeader
          title={
            <Typography
              variant="h5"
              align="center"
              sx={{ color: "#512384", fontWeight: "bold" }}
            >
              {Quiz.name}
            </Typography>
          }
        />
        {/* 📸 Image carousel with auto-slide + manual buttons */}
        {maxSteps > 0 && (
          <Box sx={{ position: "relative", overflow: "hidden" }}>
            <Swiper
              modules={[Pagination]}
              spaceBetween={0}
              slidesPerView={1}
              pagination={{ clickable: true }}
              onSwiper={(swiper :SwiperType) => {
                swiperRef.current = swiper;
              }}
              onSlideChange={(swiper: SwiperType) => {
                setActiveStep(swiper.activeIndex);
              }}
              initialSlide={activeStep}
            >
              {Quiz.quizImages.map((img: any, index: number) => (
                <SwiperSlide key={img.id || index}>
                  <div
                    style={{
                      height: "400px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <img
                      src={img.imageUrl}
                      alt={img.captionTitle}
                      style={{
                        maxHeight: "100%",
                        maxWidth: "100%",
                        objectFit: "contain",
                      }}
                      className="d-block"
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* Use your existing handlers */}
            <button
              className="carousel-control-prev btn-warning"
              type="button"
              onClick={() => swiperRef.current?.slidePrev()}
            >
              <span className="carousel-control-prev-icon"></span>
            </button>

            <button
              className="carousel-control-next"
              type="button"
              onClick={() => swiperRef.current?.slideNext()}
            >
              <span className="carousel-control-next-icon"></span>
            </button>
          </Box>
        )}
        <CardContent>
          <Box
            dangerouslySetInnerHTML={{ __html: Quiz.description }}
            sx={{ fontSize: "1.05rem", lineHeight: 1.6 }}
          />
        </CardContent>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Button
            variant="contained"
            onClick={handleStartQuiz}
            disabled={startingQuiz}
            sx={{
              backgroundColor: "rgba(81, 35, 132, 1)",
              color: "white",
              fontSize: "1rem",
              px: 4,
              py: 1,
              "&:hover": { backgroundColor: "rgba(81, 35, 132, 0.9)" },
            }}
          >
            {startingQuiz ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Starting Quiz...
              </>
            ) : (
              <>
                <i className="bi bi-play-circle me-2" />
                Start Quiz
              </>
            )}
          </Button>
        </Box>
      </Card>

      {/* ✅ Snackbar with MUI Alert */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
